import { createContext, useContext, useEffect, useState } from 'react';
import { isGrowSurfEnabled } from '../utils';
import { ROOT_ELEMEMT_ID } from '../consts';
import { useAnalytics } from './analytics';

type CopyLinkData = {
  campaign: {
    id: string;
  };
  participant: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    referralCount: number;
    shareUrl: string;
    rewards: any[];
    vanityKeys: [];
    inviteCount: number;
    monthlyRank: number;
    monthlyReferralCount: number;
    prevMonthlyRank: number;
    prevMonthlyReferralCount: number;
    rank: string;
  };
  shareType: 'copyLink';
};

interface ReferralClient {
  grsf: {
    triggerReferral: (
      data: { email: string; firstName?: string; lastName?: string },
      callback: (participant: any) => void
    ) => void;
    addParticipant: (
      data: { email: string; firstName?: string; lastName?: string },
      callback?: (participant: any) => void
    ) => void;
    open: () => void;
    getParticipantByEmail: (email: string, callback: (participant: any) => void) => void;
    /**
     * @description there can be other events rather than share
     * check https://docs.growsurf.com/integrate/javascript-web-api/api-reference#subscribe-to-event if needed and update type
     */
    subscribe: (
      type: 'share',
      handler: (data: CopyLinkData) => void
    ) => {
      unsubscribe: () => void;
    };
  };
}

export const ReferralsContext = createContext<ReferralClient>({
  grsf: {
    triggerReferral: () => {},
    addParticipant: () => {},
    open: () => {},
    getParticipantByEmail: () => {},
    subscribe: () => ({ unsubscribe: () => null }),
  },
});

function initializeGrowSurf(): Promise<ReferralClient> {
  return new Promise((resolve) => {
    if (isGrowSurfEnabled()) {
      // Initialize GrowSurf
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://app.growsurf.com/growsurf.js?v=2.0.0`;
      script.setAttribute('grsf-campaign', __GSC__);

      script.onload = () => {
        // Poll for GrowSurf initialization
        const checkGrowSurf = () => {
          if ((window as any).grsf) {
            console.log('GrowSurf initialized');
            resolve({
              grsf: (window as any).grsf,
            });
          } else {
            // Continue checking every 100ms until available
            setTimeout(checkGrowSurf, 100);
          }
        };

        // Start polling
        checkGrowSurf();

        // Add timeout to prevent infinite polling
        setTimeout(() => {
          resolve({
            grsf: {
              triggerReferral: () => {
                console.log('GrowSurf failed to initialize within timeout');
              },
              open: () => {
                console.log('GrowSurf failed to initialize within timeout');
              },
              addParticipant: () => {
                console.log('GrowSurf failed to initialize within timeout');
              },
              getParticipantByEmail: () => {
                console.log('GrowSurf failed to initialize within timeout');
              },
              subscribe: () => {
                console.log('GrowSurf failed to initialize within timeout');

                return {
                  unsubscribe: () => null,
                };
              },
            },
          });
        }, 10000); // 10 second timeout
      };

      const head = document.getElementsByTagName('head')[0];
      head.appendChild(script);

      // Set global settings
      (window as any).grsfSettings = {
        campaignId: __GSC__,
        version: '2.0.0',
      };
    } else {
      // Return stub for development/other environments
      resolve({
        grsf: {
          triggerReferral: (data: { email: string; firstName?: string; lastName?: string }) => {
            console.log('GrowSurf triggerReferral stub called: ', data);
          },
          open: () => {
            console.log('GrowSurf open stub called');
          },
          addParticipant: (data: { email: string; firstName?: string; lastName?: string }) => {
            console.log('GrowSurf addParticipant stub called: ', data);
          },
          getParticipantByEmail: (email: string) => {
            console.log('GrowSurf getParticipantByEmail stub called: ', email);
          },
          subscribe: () => {
            return {
              unsubscribe: () => null,
            };
          },
        },
      });
    }
  });
}

const disableBackgroundClicking = () => {
  const rootElement = window.document.querySelector(`body > #${ROOT_ELEMEMT_ID}`);
  const closeButtonElement = window.document.querySelector('.grsf-window-exit-button');

  if (rootElement && rootElement instanceof HTMLDivElement && closeButtonElement) {
    const initialPointerEventsCss = rootElement.style.pointerEvents;

    rootElement.style.pointerEvents = 'none';

    const handleCloseButtonClick = () => {
      rootElement.style.pointerEvents = initialPointerEventsCss;
    };

    closeButtonElement.addEventListener('click', handleCloseButtonClick);

    return () => {
      closeButtonElement.removeEventListener('click', handleCloseButtonClick);
    };
  }
};

/**
 * @description
 * since 'window.grsf' methods are not public and are based on 'this' usage proxy should be used for safe modification of grsf methods.
 * This allows to add mixins to orginal methods calls
 * */
function createProxyForGrsf(grsf: any, mixins: Record<string | symbol, ((...args: any[]) => any) | undefined>) {
  return new Proxy(grsf, {
    get(target, prop: keyof typeof grsf, receiver) {
      const value = target[prop];
      if (value instanceof Function) {
        return function (...args: any[]) {
          const mixin = mixins[prop];

          if (mixin) mixin();

          // @ts-expect-error disabled 'this' type check
          return value.apply(this === receiver ? target : this, args);
        };
      }

      return value;
    },
  });
}

export function ReferralsProvider({ children }: { children: React.ReactNode }) {
  const { amplitude } = useAnalytics();
  const [client, setClient] = useState<ReferralClient>({
    grsf: {
      triggerReferral: () => {},
      open: () => {},
      addParticipant: () => {},
      getParticipantByEmail: () => {},
      subscribe: () => ({ unsubscribe: () => null }),
    },
  });

  useEffect(() => {
    let unsubscribeEvents = () => {};

    (async () => {
      const initializedClient = await initializeGrowSurf();

      const grsf = createProxyForGrsf(initializedClient.grsf, {
        open: () => {
          const unsubscribe = disableBackgroundClicking();

          if (unsubscribe) {
            unsubscribeEvents = unsubscribe;
          }
        },
      });

      setClient({ grsf });
    })();

    return () => {
      unsubscribeEvents();
    };
  }, []);

  useEffect(() => {
    const { unsubscribe } = client.grsf.subscribe('share', (data) => {
      amplitude.track('referral_link_copied', {
        referral_code: data.participant.id,
        link: data.participant.shareUrl,
      });
    });

    return unsubscribe;
  }, [amplitude, client]);

  return <ReferralsContext.Provider value={client}>{children}</ReferralsContext.Provider>;
}

export function useReferrals() {
  return useContext(ReferralsContext);
}
